import { getCommodity, getInvoiceByKey, isSameInvoice } from '@/pages/order-center/common';
import {
  ProductCodeTypeDict,
} from '@/pages/order-center/constant';
import type {
  CommodityPriceItemType,
  SelectCodeByUserType,
  BarginPriceInfoType,
  FreewayType,
  EnableC2bFlagType,
  InvoiceTypeType,
} from '@/pages/order-center/type';
import { isEmpty } from '@hll/eapp-utils';
import { useState, useCallback, useMemo } from 'react';

interface PriceInfo {
  unpaid?: any[];
  calculateCouponTotal?: number; // 优惠券计价总金额
  priceItem?: string; // 前端未使用，透传给下单接口，转为 json 字符串存储，key 为下划线格式
  onePriceInfoPackage?: number[]; // 专享价聚合费用项
  surchargePriceItem?: any; // 额外费用项
  /** 礼品卡 */
  giftCardInfo?: {
    giftCardUseAmount?: number; // 礼品卡抵扣金额
    giftCardsBalance?: number; // 礼品卡余额
  };
}

interface OnePrice {
  hitOnePrice?: 0 | 1;
  priceItem?: string; // 前端未使用，透传给重新计价、下单接口，转为 json 字符串存储，为下划线格式
}



interface Rest {
  vehicleAttr?: string; // 前端未使用，透传给重新计价接口
  priceCalculateId?: string; // 前端未使用，透传给重新计价、下单接口
  distanceBy?: number; // 前端未使用，透传给下单接口
  tollFen?: number; // 高速费，> 0 则存在高速费
  distanceTotal?: number; // 路线路程距离
  giftCardUseAmount?: number; // 使用礼品卡抵扣金额
  giftCardsBalance?: number; // 礼品卡金额
  freewayType?: FreewayType;
  contractType?: number;
  invoiceType?: InvoiceTypeType;
  couponContractType?: number; // 传给营销券列表的合同类型 1-撮合 2-承运
  flagMap?: {
    govtSubsidyCouponFlag?: 0 | 1; // 是否支持政府补贴券 0-不支持 1-支持
  };
}

type PramsJson = string;


export default function OrderCenterSameCityCalc() {
  // ----------- 接收参数
  // 价格信息
  const [priceInfo, setPriceInfoS] = useState<PriceInfo>({});
  // 一口价
  const [onePrice, setOnePriceS] = useState<OnePrice>({});
  // 其它：一些小字段
  const [rest, setRestS] = useState<Rest>({});
  // 专享价
  const [epOnePrice, setEpOnePriceS] = useState<0 | 1>(0);

  // ----------- 请求参数
  // 下单首页请求的计价数据，暂存下来用于确认订单页
  const [paramsJson, setParamsJsonS] = useState<PramsJson>('{}');

  // 双一口价价格展示数据
  const [onePriceList, setOnePriceList] = useState<any>([]);

  // 计价id
  const [priceCalculateId, setPriceCalculateId] = useState<string>('');


  // 时间控件触发计价标识
  const [timeCalcTag, setTimeCalcTag] = useState<boolean>(false);
  const [enableC2BFlag, setEnableC2BFlag] = useState<EnableC2bFlagType>();


  const [selectCodeByUser, setSelectCodeByUser] = useState<SelectCodeByUserType>();


  // 用户议价数据
  const [userBargainPriceInfo, setUserBargainPriceInfo] = useState<BarginPriceInfoType>();

  const [carpoolBargainPriceInfo, setCarpoolBargainPriceInfo] = useState<BarginPriceInfoType>();

  const [lindanCarBargainPriceInfo, setLindanCarBargainPriceInfo] = useState<BarginPriceInfoType>();

  // 价格标签
  const [priceLabelText, setPriceLabelText] = useState('');

  // 是否命中新大车一口价
  const [relyOnXbPrice, setRelyOnXbPrice] = useState<0 | 1>(0);
  const [commodityPriceList, setCommodityPriceList] =
    useState<CommodityPriceItemType[]>();

  // const [selectedInvoice, setSelectedInvoice] = useState<InvoiceItemType>();
  // invoiceType-contractType 格式
  const [userSelectedInvoiceType, setUserSelectedInvoiceType] = useState<string>();

  const [agreeGoodsTransportAgreement, setAgreeGoodsTransportAgreement] =
    useState<boolean>(false);

  const setTimeCalcTagCb = useCallback((val) => {
    setTimeCalcTag(val);
  }, []);

  const setPriceCalculateIdCb = useCallback((val) => {
    setPriceCalculateId(val);
  }, []);

  const setOnePriceListCb = useCallback((val) => {
    setOnePriceList(val);
  }, []);

  const setPriceInfo = useCallback((val: PriceInfo) => {
    setPriceInfoS(val);
  }, []);

  const setOnePrice = useCallback((val) => {
    setOnePriceS(val);
  }, []);

  const setEpOnePrice = useCallback((val) => {
    setEpOnePriceS(val);
  }, []);

  const setRest = useCallback((val: Rest) => {
    setRestS(val);
  }, []);

  const setParamsJson = useCallback((val) => {
    setParamsJsonS(val);
  }, []);

  // 默认选中的商品 - 用户无操作时使用。 默认没返回则选中第一个。
  const defaultCommodity = useMemo(() => {
    const findSelectedCommodity = commodityPriceList?.find((item) => !!item?.displayControlFactor?.selected) || commodityPriceList?.[0];
    return findSelectedCommodity;
  }, [commodityPriceList])
  // 页面显示的选中品类变量。
  const currentSelectCode = useMemo(() => {
    const hasUserSelectedCommodity = commodityPriceList?.find((item) => item?.commodityInfo?.productCode === selectCodeByUser);
    if (!selectCodeByUser || !hasUserSelectedCommodity) {
      // 用户选的商品是否存在 不存在也是用默认商品。
      // 用户未选择，则使用默认选中的商品；且存在默认选中的商品，则使用默认选中的商品。
      return defaultCommodity?.commodityInfo?.productCode;
    }
    return selectCodeByUser;
  }, [commodityPriceList, defaultCommodity?.commodityInfo?.productCode, selectCodeByUser]);

  const currentCommodityPrice = useMemo(() => {
    const commodity = getCommodity(currentSelectCode, commodityPriceList);
    return commodity;
  }, [commodityPriceList, currentSelectCode]);

  // 命中议价用车商品
  const userBargainCommodity = useMemo(() => {
    const commodity = commodityPriceList?.find(
      (e) =>
        e.commodityInfo?.productCode ===
        ProductCodeTypeDict?.kv?.ATTV_PRD_CATE_BARGAIN
    );
    return commodity;
  }, [commodityPriceList]);


  // 命中拼车出价商品
  const carpoolBargainCommodity = useMemo(() => {
    const commodity = commodityPriceList?.find(
      (e) =>
        e.commodityInfo?.productCode ===
        ProductCodeTypeDict?.kv?.ATTV_PRD_CATE_CARPOOL && e.displayControlFactor?.bargainSupport === 1
    );
    return commodity;
  }, [commodityPriceList]);

  // 当前是否是议价用车
  const currentIsUserBargain = useMemo(() => {
    return (
      currentCommodityPrice?.commodityInfo?.productCode === ProductCodeTypeDict?.kv?.ATTV_PRD_CATE_BARGAIN
    );
  }, [currentCommodityPrice?.commodityInfo?.productCode]);


  // 当前是否选中拼车-出价
  const currentIsCarPoolBargain = useMemo(() => {
    return (
      currentCommodityPrice?.commodityInfo?.productCode === ProductCodeTypeDict?.kv?.ATTV_PRD_CATE_CARPOOL && currentCommodityPrice?.displayControlFactor?.bargainSupport === 1
    );
  }, [currentCommodityPrice?.displayControlFactor?.bargainSupport, currentCommodityPrice?.commodityInfo?.productCode]);

  // 当前是否拼车商品
  const currentIsCarPoolCommodity = useMemo(() => {
    return (
      currentCommodityPrice?.commodityInfo?.productCode === ProductCodeTypeDict?.kv?.ATTV_PRD_CATE_CARPOOL
    );
  }, [currentCommodityPrice?.commodityInfo?.productCode])

  // 路线路程距离
  const distanceTotal = useMemo(() => {
    if (currentCommodityPrice) {
      return currentCommodityPrice?.routeInfo?.distanceTotal;
    }
    return rest?.distanceTotal;
  }, [currentCommodityPrice, rest?.distanceTotal]);

  const priceDetail = useMemo(() => {
    return currentCommodityPrice?.commodityPriceDetail;
  }, [currentCommodityPrice]);

  const currentInvoiceOption = useMemo(() => {
    if (!currentCommodityPrice) {
      return undefined;
    }
    // 只有一个时，默认选中
    const list = currentCommodityPrice?.invoiceTypeSelectConfig?.invoiceTypeList
    if (list?.length === 1) {
      return list?.[0];
    }
    if (!userSelectedInvoiceType) {
      return undefined;
    }
    // 需要用合同类型和发票类型同时匹配才能认为是选中
    const invoiceExist = list?.find(e => isSameInvoice({ invoice1: e, invoice2: getInvoiceByKey(userSelectedInvoiceType) }))
    return invoiceExist;
  }, [currentCommodityPrice, userSelectedInvoiceType]);

  const currentIsLingDan = useMemo(() => {
    return currentCommodityPrice?.commodityInfo?.productCode === ProductCodeTypeDict?.kv?.ATTV_PRD_CATE_CARGO_PRICING && currentCommodityPrice?.displayControlFactor?.bargainSupport === 1;
  }, [currentCommodityPrice?.commodityInfo?.productCode, currentCommodityPrice?.displayControlFactor?.bargainSupport]);

  const updatePriceLabelText = useCallback(
    (val: string) => {
      setPriceLabelText(val);
    },
    [setPriceLabelText]
  );

  const calcReset = () => {
    setPriceInfo({});
    setOnePrice({});
    setRest({});
    setRelyOnXbPrice(0);
    setCommodityPriceList(undefined);
    updatePriceLabelText('');
    setUserSelectedInvoiceType(undefined);
    setAgreeGoodsTransportAgreement(false);
  };


  const resetCarpoolBargainInfo = useCallback(() => {
    setSelectCodeByUser(undefined);
    setCarpoolBargainPriceInfo(undefined);
  }, []);

  const updateSelectCodeByUser = useCallback((q: SelectCodeByUserType) => {
    setSelectCodeByUser(q);
  }, []);
  const updateBarginPriceInfo = useCallback(
    (b?: BarginPriceInfoType) => setUserBargainPriceInfo(b),
    [setUserBargainPriceInfo]
  );

  const updateCarpoolBargainPriceInfo = useCallback(
    (b?: BarginPriceInfoType) => setCarpoolBargainPriceInfo(b),
    [setCarpoolBargainPriceInfo]
  );

  const updateLindanCarBargainPriceInfo = useCallback(
    (b?: BarginPriceInfoType) => setLindanCarBargainPriceInfo(b),
    [setLindanCarBargainPriceInfo]
  );
  const resetUserBargainInfo = useCallback(() => {
    setSelectCodeByUser(undefined);
    setUserBargainPriceInfo(undefined);
  }, []);
  const resetCargoPricingInfo = useCallback(() => {
    setSelectCodeByUser(undefined);
    updateLindanCarBargainPriceInfo(undefined);
  }, [updateLindanCarBargainPriceInfo]);

  const resetCurrentCommodityCode = useCallback(() => {
    if (currentIsCarPoolCommodity) {
      resetCarpoolBargainInfo();
    } else if (currentIsUserBargain) {
      resetUserBargainInfo()
    } else if (currentIsLingDan) {
      resetCargoPricingInfo();
    }
    setSelectCodeByUser(undefined);
  }, [
    currentIsCarPoolCommodity,
    currentIsUserBargain,
    currentIsLingDan,
    resetCarpoolBargainInfo,
    resetCargoPricingInfo,
    resetUserBargainInfo,
  ]);

  const hasFilledBargainPrice = useMemo(() => {
    if (!currentCommodityPrice || isEmpty(currentCommodityPrice?.commodityPriceDetail?.finalPrice)) {
      return false;
    }

    // 拼车-出价-未填(无推荐价)价格则不可下单
    const carpoolBargainNotFilled =
      currentIsCarPoolBargain &&
      !currentCommodityPrice?.commodityPriceDetail?.finalPrice;
    if (carpoolBargainNotFilled) {
      return false;
    }

    const userBargainNotFilled =
      currentIsUserBargain &&
      !currentCommodityPrice?.commodityPriceDetail?.finalPrice;

    if (userBargainNotFilled) {
      return false;
    }

    const isLingdanFilled = currentIsLingDan && !currentCommodityPrice?.commodityPriceDetail?.finalPrice;
    if (isLingdanFilled) {
      return false;
    }
    return true;
  }, [currentCommodityPrice, currentIsCarPoolBargain, currentIsUserBargain, currentIsLingDan])

  return {
    priceInfo,
    setPriceInfo,

    onePrice,
    setOnePrice,

    epOnePrice,
    setEpOnePrice,

    rest,
    setRest,

    paramsJson,
    setParamsJson,

    onePriceList,
    setOnePriceListCb,

    priceCalculateId,
    setPriceCalculateIdCb,

    timeCalcTag,
    setTimeCalcTagCb,

    calcReset,

    /** 是否命中新大车一口价 */
    relyOnXbPrice,
    setRelyOnXbPrice,
    /** 返回的商品-价格列表 */
    commodityPriceList,
    setCommodityPriceList,
    /** 当前的商品价格参数 */
    currentCommodityPrice,
    /** 是否命中议价用车 */
    userBargainCommodity,
    carpoolBargainCommodity,
    selectCodeByUser,
    currentSelectCode,
    userBargainPriceInfo,
    // 重置用户议价数据
    resetUserBargainInfo,
    /** 当前选中是否拼车 商品 */
    currentIsCarPoolCommodity,
    /** 当前选中的是否命中拼车-出价场景 */
    currentIsCarPoolBargain,
    updateBarginPriceInfo,
    updateSelectCodeByUser,
    currentIsUserBargain,
    priceLabelText,
    updatePriceLabelText,
    priceDetail,
    distanceTotal,
    carpoolBargainPriceInfo,
    updateCarpoolBargainPriceInfo,
    resetCarpoolBargainInfo,
    resetCurrentCommodityCode,
    hasFilledBargainPrice,
    userSelectedInvoiceType,
    setUserSelectedInvoiceType,
    currentInvoiceOption,
    agreeGoodsTransportAgreement,
    setAgreeGoodsTransportAgreement,
    enableC2BFlag,
    setEnableC2BFlag,
    currentIsLingDan,
    lindanCarBargainPriceInfo,
    updateLindanCarBargainPriceInfo,
    defaultCommodity,
  };
}
